import {
    Autocomplete,
    Box,
    Button,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography
} from "@mui/material"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"
import {useTranslation} from "react-i18next"
import {Service} from "crqlar-react-common/dist/models/service/service.model"
import {useMemo, useState} from "react"
import {useNavigate} from "react-router-dom"
import {Resource} from "crqlar-react-common/dist/models/resource/resource.model"
import {
    Availability
} from "crqlar-react-common/dist/models/availability/availability.model"
import {
    formatDateDMYHM,
    formatDateMDHM,
    isToday
} from "crqlar-react-common/dist/utils/date.util"
import {
    AvailabilityException
} from "crqlar-react-common/dist/models/availability/availabilityException.model";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import {computeRRule, RRuleOccurrence} from "./utils";
import {
    ServiceService
} from "crqlar-react-common/dist/services/service/service.service";

interface Props {
    resources: Resource[]
    availabilities: Availability[]
    exceptions: AvailabilityException[]
    service: Service
    updateField: (field: keyof Service, value: any) => void
}

const keyOf = (resourceId: string, availabilityId: string) => `${resourceId}::${availabilityId}`
const AvailabilitySection: React.FC<Props> = ({
                                                  resources,
                                                  availabilities,
                                                  exceptions,
                                                  service,
                                                  updateField,
                                              }) => {
    const {t} = useTranslation()
    const navigate = useNavigate()

    const [addingForResource, setAddingForResource] = useState<string | null>(null)

    const bindings = useMemo(
        () => service.availabilityBindings ?? [],
        [service]
    )

    const usedKeys = useMemo(() => {
        const s = new Set<string>()
        for (let i = 0; i < bindings.length; i++) {
            const b = bindings[i]
            if (b.availabilityIds && b.availabilityIds.length > 0) {
                b.availabilityIds.forEach(id => s.add(keyOf(b.resourceId, id)))
            }
        }
        return s
    }, [bindings])

    const setBindings = (
        next: Array<{
            resourceId: string;
            availabilityName: string;
            availabilityIds: string[]
        }>
    ) => updateField("availabilityBindings", next)

    const replaceAt = (i: number, newAvailabilityId: string, newAvailabilityName: string) => {
        const next = bindings.slice()
        next[i] = {
            ...next[i],
            availabilityName: newAvailabilityName,
            availabilityIds: [newAvailabilityId],
        }
        setBindings(next)
    }

    const addOneForResource = (resourceId: string, availabilityId: string, availabilityName: string) => {
        const next = [
            ...bindings,
            {
                resourceId,
                availabilityName,
                availabilityIds: [availabilityId],
            },
        ]

        setBindings(next)
        setAddingForResource(null)

        ServiceService.updateService({
            ...service,
            availabilityBindings: next ?? [],
        })
    }

    const removeAt = (i: number) => {
        const next = bindings.slice()
        next.splice(i, 1)
        setBindings(next)
        ServiceService.updateService({
            ...service,
            availabilityBindings: next ?? [],
        })
    }

    return (
        <>
            <Typography variant="h6" textAlign="start" mt={4}>
                {t("Weekly Planner Availabilities")}
            </Typography>

            {resources.map((resource, resourceIndex) => {
                const resourceBindings = bindings.filter(b => b.resourceId === resource.id)
                const assignedAvailabilities = resourceBindings
                    .map(binding => {
                        const availabilityId = binding.availabilityIds?.[0]
                        return availabilities.find(a => a.id === availabilityId)
                    })
                    .filter(a => a !== undefined) as Availability[]


                return (
                    <Box
                        key={resource.id ?? resourceIndex}
                        sx={{my: 2}}>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'end',
                            justifyContent: 'space-between',
                            borderBottom: "1px solid",
                            borderColor: "rgba(0, 0, 0, 0.12)",
                            pb: 0.5,
                            flex: 1,
                        }}>
                            <Typography variant="subtitle1">
                                {resource.name ?? t("Resource")}
                            </Typography>
                            <Stack direction={"row"} spacing={1}>
                                <Button
                                    variant="outlined"
                                    endIcon={<OpenInNewIcon/>}
                                    onClick={() => {
                                        const url = `/system/availability/${resource.id}`
                                        navigate(url)
                                    }}
                                    sx={{whiteSpace: "nowrap"}}
                                    size={"small"}
                                >
                                    {t("Edit availability")}
                                </Button>
                            </Stack>
                        </Box>

                        {assignedAvailabilities.length === 0 && addingForResource !== resource.id ? (
                            <Typography variant="body2" sx={{
                                mt: 1,
                                ml: 1,
                                color: "text.secondary"
                            }}>
                                {t("No availabilities assigned yet")}
                            </Typography>
                        ) : (
                            assignedAvailabilities.map((availability, idx) => {
                                const binding = resourceBindings[idx]
                                const bindingIndex = bindings.indexOf(binding)

                                // Get all availabilities for this resource that aren't already used
                                // Include the current availability
                                const availableForResource = availabilities
                                    .filter(a => a.entityId === resource.id)
                                    .filter(a => {
                                        const key = keyOf(resource.id!, a.id!)
                                        return !usedKeys.has(key) || a.id === availability.id // Allow current
                                    })

                                return (
                                    <AvailabilityRow
                                        key={availability.id}
                                        availability={availability}
                                        availableOptions={availableForResource}
                                        exceptions={exceptions}
                                        onUpdate={(newAvailabilityId) => {
                                            const newAvailability = availableForResource.find(a => a.id === newAvailabilityId)
                                            if (newAvailability) {
                                                replaceAt(bindingIndex, newAvailabilityId, newAvailability.name ?? "")
                                            }
                                        }}
                                        onDelete={() => removeAt(bindingIndex)}
                                    />
                                )
                            })
                        )}

                        {addingForResource === resource.id ? (
                            <Box sx={{
                                mt: 1,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                backgroundColor: "rgba(0, 0, 0, 0.04)",
                                padding: "4px 8px",
                                borderRadius: "4px",
                                minHeight: "38px"
                            }}>
                                <Autocomplete
                                    id={`availability-add-${resource.id}`}
                                    options={
                                        availabilities
                                            .filter(a => a.entityId === resource.id)
                                            .filter(a => {
                                                const key = keyOf(resource.id!, a.id!)
                                                return !usedKeys.has(key)
                                            })
                                            .sort((a, b) => {
                                                const aName = a.name ?? ""
                                                const bName = b.name ?? ""
                                                const nameCmp = aName.localeCompare(bName)
                                                if (nameCmp !== 0) return nameCmp

                                                const {firstDate: aFirst} = computeRRule(a, exceptions)
                                                const {firstDate: bFirst} = computeRRule(b, exceptions)

                                                const aTime = aFirst ? new Date(aFirst).getTime() : 0
                                                const bTime = bFirst ? new Date(bFirst).getTime() : 0

                                                return aTime - bTime
                                            })
                                    }
                                    value={null}
                                    isOptionEqualToValue={(a, b) => a.id === b.id}
                                    getOptionLabel={(opt) => {
                                        const {
                                            firstDate,
                                            lastDate
                                        } = computeRRule(opt, exceptions)
                                        const isClosed = opt.availabilityTypeClean?.toLowerCase() === "closed"
                                        const isWeekly = opt.rrule?.toLowerCase().includes("weekly")

                                        const tags: string[] = []
                                        if (isClosed) tags.push(t("Closed"))
                                        if (isWeekly) tags.push(t("Weekly"))

                                        const tagStr = tags.length > 0 ? ` [${tags.join(", ")}]` : ""
                                        return `${opt.name ?? t("Unnamed Availability")}${tagStr} ${formatDateMDHM(firstDate)} - ${formatDateMDHM(lastDate)}`
                                    }}
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            size="small"
                                            placeholder={t("Select availability")}
                                        />
                                    )}
                                    onChange={(_, item) => {
                                        if (item?.id) {
                                            addOneForResource(resource.id!, item.id, item.name ?? "")
                                        }
                                    }}
                                    clearOnBlur={false}
                                    fullWidth
                                    sx={{flex: 1}}
                                />


                                <Button
                                    variant={"text"}
                                    size={"small"}
                                    onClick={() => setAddingForResource(null)}
                                >
                                    {t("Cancel")}
                                </Button>
                            </Box>
                        ) : (
                            <Button
                                startIcon={<AddIcon/>}
                                onClick={() => setAddingForResource(resource.id!)}
                                size="small"
                                sx={{mt: 1}}
                            >
                                {t("Add availability")}
                            </Button>
                        )}
                    </Box>
                )
            })}
        </>
    )
}

const AvailabilityRow: React.FC<{
    availability: Availability,
    availableOptions: Availability[],
    exceptions: AvailabilityException[],
    onUpdate: (newAvailabilityId: string) => void,
    onDelete: () => void
}> = ({availability, availableOptions, exceptions, onUpdate, onDelete}) => {
    const {t} = useTranslation()
    console.log(availability)
    const {lastDate} = computeRRule(availability, exceptions);
    const [editing, setEditing] = useState(false)

    const [expanded, setExpanded] = useState(false);

    const {occurrences} = computeRRule(availability, exceptions);
    if (occurrences.length === 0) return null;

    const total = occurrences.length;
    const rows: (RRuleOccurrence | "ellipsis")[] = [];

    if (expanded || total <= 2) {
        rows.push(...occurrences);
    } else {
        rows.push(occurrences[0], "ellipsis", occurrences[total - 1]);
    }

    return (
        <Box sx={{
            display: "flex",
            flexDirection: "column"
        }}>
            <Box sx={{
                mt: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                backgroundColor: "rgba(0, 0, 0, 0.04)",
                padding: "4px 8px",
                borderRadius: "4px",
                minHeight: "38px"
            }}>

                {editing ? <>
                    <Autocomplete
                        id={`availability-inline-${availability.id}`}
                        options={[...availableOptions].sort((a, b) => {
                            const aName = a.name ?? ""
                            const bName = b.name ?? ""
                            const nameCmp = aName.localeCompare(bName)
                            if (nameCmp !== 0) return nameCmp

                            const {firstDate: aFirst} = computeRRule(a, exceptions)
                            const {firstDate: bFirst} = computeRRule(b, exceptions)

                            const aTime = aFirst ? new Date(aFirst).getTime() : 0
                            const bTime = bFirst ? new Date(bFirst).getTime() : 0

                            return aTime - bTime
                        })}
                        value={availability}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        getOptionLabel={(opt) => {
                            const {
                                firstDate,
                                lastDate
                            } = computeRRule(opt, exceptions)
                            const isClosed = opt.availabilityTypeClean?.toLowerCase() === "closed"
                            const isWeekly = opt.rrule?.toLowerCase().includes("weekly")

                            const tags: string[] = []
                            if (isClosed) tags.push(t("Closed"))
                            if (isWeekly) tags.push(t("Weekly"))

                            const tagStr = tags.length > 0 ? ` [${tags.join(", ")}]` : ""
                            return `${opt.name ?? t("Unnamed Availability")}${tagStr} ${formatDateMDHM(firstDate)} - ${formatDateMDHM(lastDate)}`
                        }}
                        renderInput={params => (
                            <TextField {...params} fullWidth size="small"/>
                        )}
                        onChange={(_, item) => {
                            if (item?.id) {
                                onUpdate(item.id)
                            }
                            setEditing(false)
                        }}
                        clearOnBlur={false}
                        fullWidth
                        sx={{flex: 1}}
                    />


                    <Button variant={"text"} size={"small"}
                            onClick={() => setEditing(false)}>
                        {t("Cancel")}
                    </Button>

                    <Button
                        variant={"text"}
                        size={"small"}
                        color={"error"}
                        onClick={() => {
                            onDelete()
                            setEditing(false)
                        }}
                    >
                        {t("Delete")}
                    </Button>

                </> : <>
                    <Typography variant="body1">
                        {availability.availabilityTypeClean?.toLowerCase() === "closed" && (
                            <Typography component="span"
                                        fontWeight="600">
                                {t("Closed")} |{" "}
                            </Typography>
                        )}
                        {availability.name ?? t("Unnamed Availability")}
                        {availability.rrule && (
                            <>
                                {" - "}
                                <Typography component="span"
                                            fontWeight="600">
                                    {availability.rrule.toLowerCase().includes("daily") && t("Daily")}
                                    {availability.rrule.toLowerCase().includes("weekly") && t("Weekly")}
                                </Typography>

                                {lastDate && (
                                    <Typography
                                        component="span"
                                        fontWeight="600">
                                        {" - "}{t("Until")} {formatDateMDHM(lastDate)}
                                    </Typography>
                                )}
                            </>
                        )}
                    </Typography>

                    <IconButton size={"small"}
                                sx={{ml: "auto"}} onClick={() => setEditing(true)}>
                        <EditIcon
                            sx={{width: 16, height: 16}}/>
                    </IconButton>
                </>}
            </Box>

            <Box>
                <Box sx={{mt: 0.5, ml: 2}}>
                    <Table
                        size="small"
                        sx={{
                            width: "auto",
                            borderCollapse: "collapse",
                            "& tbody tr:nth-of-type(odd)": {
                                backgroundColor: "rgba(0, 0, 0, 0.02)",
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    sx={{fontWeight: 600}}>{t("Start")}</TableCell>
                                <TableCell
                                    sx={{fontWeight: 600}}>{t("End")}</TableCell>
                                <TableCell
                                    sx={{fontWeight: 600}}>{t("Capacity")}</TableCell>
                                <TableCell
                                    sx={{fontWeight: 600}}>{t("Closed")}</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {rows.map((row, index) => {
                                if (row === "ellipsis") {
                                    return (
                                        <TableRow key={`ellipsis-${index}`}>
                                            <TableCell colSpan={4} sx={{py: 0.25}}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: "primary.main",
                                                        cursor: "pointer"
                                                    }}
                                                    onClick={() => setExpanded(true)}
                                                >
                                                    ...
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    );
                                }

                                const occurrence = row;
                                const fontWeight = isToday(occurrence.start) || isToday(occurrence.end) ? 800 : 400;

                                return (
                                    <TableRow key={index}>
                                        <TableCell sx={{pr: 2}}>
                                            <Typography variant="body2"
                                                        fontWeight={fontWeight}
                                                        sx={{color: "text.secondary"}}>
                                                {formatDateDMYHM(occurrence.start)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{pr: 2}}>
                                            <Typography variant="body2"
                                                        fontWeight={fontWeight}
                                                        sx={{color: "text.secondary"}}>
                                                {formatDateDMYHM(occurrence.end)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{pr: 2}}>
                                            <Typography variant="body2"
                                                        fontWeight={fontWeight}
                                                        sx={{color: "text.secondary"}}>
                                                {occurrence.capacity ?? ""}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{}}>
                                            <Typography variant="body2"
                                                        fontWeight={fontWeight}
                                                        sx={{color: "text.secondary"}}>
                                                {occurrence.isClosed ? t("Closed") : ""}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}

                            {total > 2 && expanded && (
                                <TableRow>
                                    <TableCell colSpan={4} sx={{py: 0.25}}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: "primary.main",
                                                cursor: "pointer"
                                            }}
                                            onClick={() => setExpanded(false)}
                                        >
                                            {t("Show less")}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Box>
            </Box>
        </Box>
    )
}


export default AvailabilitySection
